import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldDefinition } from '../../shared/types';
import { OptGroup, SecInfo, groupsForContainer, withSubs , pickerGroups } from './fieldGroups';
import FieldPicker from './FieldPicker';
import { asksNothing, flattenWidgetTree } from '../../shared/types';
import { Link } from 'react-router-dom';
import {
  CatalogEntry, CatalogField, CredentialField, CredentialProvider, MondayBoard, MondayColumn,
  SavedCredential, adminMondayBoards, adminMondayColumns, adminMondaySubitemColumns,
  getIntegrationCatalog, listCredentials,
} from '../../api/admin';
import {
  ConnectionConditionGroup, ConnectionRow, ConnectionType, createConnectionApi, deleteConnectionApi,
  getFormBuilder, listForms, listFormConnections, testConnectionApi,
  updateConnectionApi,
} from '../../api/forms';
import { workingDocumentOf } from '../../api/forms';
import { useToast } from '../../components/Toast';
import { useDirty } from '../../components/unsaved';
import { useFormWorkspace } from './FormWorkspace';
import { documentSummary } from './documentSummary';

/** The bare shape a spec field of kind 'form' needs to offer a choice. */
export interface PickableForm { id: number; name: string; status: string }

/**
 * Questions whose answer is PICKED from a list, not typed.
 *
 * All three store an option's value and show its label, so a condition on any
 * of them belongs in a dropdown — a text box there asked whoever was writing
 * the rule to know the stored value, which is exactly what the picker exists
 * to spare them. This is the Logic page's own rule (`CHOICE` there), lifted to
 * where the condition rows live so both pages answer it the same way. A
 * checkbox is left out on purpose: it holds a boolean, not one of its
 * `options`.
 */
const CHOICE_TYPES = new Set(['select', 'radio', 'multiselect']);

/**
 * The operators that weigh one whole answer — the ones a dropdown of the
 * question's own answers can fill in. `contains` and the number and date
 * comparisons are deliberately absent: they test a fragment or a magnitude.
 */
const PICKED_OPS = new Set(['is', 'isnot', 'includes', 'notincludes']);

/** The answers a question offers, or null where it is not one that offers any. */
export function answerOptions(f: FieldDefinition | undefined): FieldDefinition['options'] | null {
  return f && CHOICE_TYPES.has(f.input_type) && f.options?.length ? f.options : null;
}

/**
 * A stored answer in the words the form shows for it.
 *
 * The value that reaches a condition is an option's VALUE (`dropShipPlusInstall`),
 * and everywhere else in the admin that answer reads as its text ("Drop Ship +
 * Installation"). A value the question no longer offers is returned as itself:
 * it is still what the condition tests, and inventing words for it would hide
 * that the rule has gone stale.
 */
export function answerText(f: FieldDefinition | undefined, value: string | undefined): string {
  const opts = answerOptions(f);
  return opts?.find((o) => o.value === value)?.text || value || '';
}

/**
 * A connection's condition in words.
 *
 * Named by the question's LABEL, not its key: the builder above is a picker of
 * labels, so answering with "fillMode" makes the admin translate their own
 * choice back. The key is the fallback for a question since deleted. The value
 * gets the same treatment for a picked answer, for the same reason.
 */
export function describeGroup(group: ConnectionConditionGroup, fields: FieldDefinition[] = []): string {
  const def = (key: string) => fields.find((f) => f.key === key);
  const label = (key: string) => def(key)?.label || key;
  const said = (c: ConnectionConditionGroup['when'][number]) => answerText(def(c.field), c.value);
  const one = (c: ConnectionConditionGroup['when'][number]) => (
    c.op === 'filled' ? `${label(c.field)} is answered`
      : c.op === 'empty' ? `${label(c.field)} is blank`
        : c.op === 'isnot' ? `${label(c.field)} is not “${said(c)}”`
          : c.op === 'includes' ? `${label(c.field)} includes “${said(c)}”`
            : c.op === 'notincludes' ? `${label(c.field)} does not include “${said(c)}”`
              : c.op === 'gt' ? `${label(c.field)} is more than ${said(c)}`
                : c.op === 'lt' ? `${label(c.field)} is less than ${said(c)}`
                  : c.op === 'gte' ? `${label(c.field)} is at least ${said(c)}`
                    : c.op === 'lte' ? `${label(c.field)} is at most ${said(c)}`
                      : c.op === 'before' ? `${label(c.field)} is before ${said(c)}`
                        : c.op === 'after' ? `${label(c.field)} is after ${said(c)}`
                          : c.op === 'contains' ? `${label(c.field)} contains “${said(c)}”`
                            : `${label(c.field)} is “${said(c)}”`
  );
  return (group.when || []).map(one).join(group.match === 'any' ? ' or ' : ' and ');
}

/**
 * "Only run when…" — the same condition shape the Logic page writes, so an
 * admin who has built a form rule already knows how to build one of these.
 */
export function RunWhenEditor({
  value, fields, sections, extraGroups, extraFields, onChange, label, emptyHint,
}: {
  value: ConnectionConditionGroup | null | undefined;
  fields: FieldDefinition[];
  /**
   * The form's sections and cards. Routing on the wrong one of two questions
   * that share a label sends every submission the wrong way, silently, so the
   * question picker is arranged the way the form is built when the caller can
   * say how — the Logic page's arrangement, since these are its rows.
   */
  sections?: SecInfo[];
  /**
   * Questions from elsewhere, after the form's own — the order a child form
   * continues, keyed `parent.<key>`. Groups rather than fields so they keep
   * their own arrangement instead of falling into "Other fields".
   */
  extraGroups?: OptGroup[];
  /**
   * The definitions behind `extraGroups`, where the caller has them — the
   * parents' questions, keyed `parent.<key>`. A group is a list of names; the
   * value dropdown needs the question itself, to know it is a dropdown and
   * what it offers. Omitting them costs a `parent.*` row its value dropdown
   * and nothing else.
   */
  extraFields?: FieldDefinition[];
  onChange: (v: ConnectionConditionGroup | null) => void;
  /**
   * What the condition decides, in this caller's words.
   *
   * The rows are the same rows wherever they appear — that is the point, an
   * admin who has written one on the Logic page already knows how — but on a
   * connection they answer "does this step happen?" and on a workflow branch
   * they answer "which way does this go?". Defaults to the connection's
   * question, which is what every existing caller means.
   */
  label?: string;
  emptyHint?: string;
}) {
  const rows = value?.when || [];
  /**
   * The questions, in the form's own shape when a caller supplied one and as
   * one flat list otherwise. Either way each carries its key, because a label
   * on its own is not an identity.
   */
  const questionGroups = useMemo<OptGroup[]>(() => {
    const askable = fields.filter((f) => !asksNothing(f.input_type));
    const own = !sections?.length
      ? [{ label: 'Questions', opts: askable.flatMap(withSubs) }]
      : [...new Set(askable.map((f) => f.scope))].flatMap((scope) => groupsForContainer(
        scope, askable, sections, withSubs,
        scope === 'order' ? {} : { prefix: `Every ${scope}` }
      ));
    return [...own, ...(extraGroups || [])];
  }, [fields, sections, extraGroups]);
  /**
   * Every question a row can name, by key — this form's own and whatever the
   * caller added. Only a whole question is here: a composite's part
   * (`shipTo.city`) is a picker option, not a definition, and is typed as it
   * always was.
   */
  const defByKey = useMemo(
    () => new Map([...fields, ...(extraFields || [])].map((f) => [f.key, f])),
    [fields, extraFields]
  );
  const match: 'all' | 'any' = value?.match === 'any' ? 'any' : 'all';
  const set = (when: ConnectionConditionGroup['when'], m: 'all' | 'any' = match) =>
    onChange(when.length ? { match: m, when } : null);

  return (
    <div className="ad-field" style={{ marginTop: 14 }}>
      <label>{label || 'Only run when…'}</label>
      {!rows.length && (
        <small>
          {emptyHint || (
            'No condition — this connection runs for every submission. Add one to make it depend on an answer, '
            + 'for example only emailing the customer when the sales rep did not take the details themselves.'
          )}
        </small>
      )}
      {rows.length > 1 && (
        <div style={{ margin: '6px 0' }}>
          <select
            value={match} style={{ maxWidth: 220 }}
            onChange={(e) => set(rows, e.target.value === 'any' ? 'any' : 'all')}
          >
            <option value="all">every condition below holds</option>
            <option value="any">any condition below holds</option>
          </select>
        </div>
      )}
      {rows.map((row, i) => {
        const patch = (p: Partial<typeof row>) =>
          set(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
        const needsValue = row.op !== 'filled' && row.op !== 'empty';
        const watched = defByKey.get(row.field);
        /**
         * A dropdown of the answers, for the operators that weigh one answer
         * against another. `contains`, and the number and date comparisons,
         * keep their text box: they compare a fragment or a magnitude, not an
         * option's identity, and turning them into a picker would take away
         * the only way to write them.
         */
        const valueOptions = PICKED_OPS.has(row.op) ? answerOptions(watched) : null;
        /**
         * A saved value the list does not hold — an option renamed away since,
         * or one typed by hand before this dropdown existed — stays offered as
         * itself. Dropping it would let merely OPENING a branch change which
         * submissions take which path.
         */
        const stray = valueOptions && row.value && !valueOptions.some((o) => o.value === row.value)
          ? row.value : '';
        return (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <FieldPicker
              value={row.field} style={{ maxWidth: 240 }} groups={questionGroups} placeholder="— choose a question —"
              onChange={(field) => {
                // The value belonged to the question that was named a moment
                // ago. Carried over, it is either a wrong test or an option
                // value sitting in a text question — so a picked answer starts
                // on its question's first one and anything else starts empty.
                const f = defByKey.get(field);
                patch({ field, value: answerOptions(f)?.[0]?.value ?? '' });
              }}
            />
            <select
              value={row.op} style={{ maxWidth: 150 }}
              onChange={(e) => patch({ op: e.target.value as typeof row.op })}
            >
              <option value="is">is</option>
              <option value="isnot">is not</option>
              {/* A question that takes more than one answer stores a list, and
                  `is` weighs the whole list. Same operator, same evaluator as
                  the Logic page's — this editor writes the same shape. */}
              <option value="includes">includes</option>
              <option value="notincludes">does not include</option>
              {/* P4's operators, evaluated by the same server code that runs
                  a form rule; numbers compare as numbers, dates as dates, and
                  `contains` searches text (matching a list's items whole). */}
              <option value="gt">is more than</option>
              <option value="lt">is less than</option>
              <option value="gte">is at least</option>
              <option value="lte">is at most</option>
              <option value="before">is before</option>
              <option value="after">is after</option>
              <option value="contains">contains</option>
              <option value="filled">is answered</option>
              <option value="empty">is blank</option>
            </select>
            {needsValue && (valueOptions ? (
              <select
                value={row.value ?? ''} style={{ maxWidth: 220 }} aria-label="Answer to test for"
                onChange={(e) => patch({ value: e.target.value })}
              >
                <option value="">(blank)</option>
                {valueOptions.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                {stray && <option value={stray}>{stray} — not one of the answers</option>}
              </select>
            ) : (
              <input
                value={row.value ?? ''} placeholder="value" style={{ maxWidth: 200 }}
                onChange={(e) => patch({ value: e.target.value })}
              />
            ))}
            <button
              className="ad-btn small danger" title="Remove this condition"
              onClick={() => set(rows.filter((_, j) => j !== i))}
            >&times;</button>
          </div>
        );
      })}
      <button
        className="ad-btn small secondary"
        onClick={() => set([...rows, { field: '', op: 'is', value: '' }])}
      >+ Add condition</button>
      {rows.some((r) => !r.field) && (
        <small style={{ color: '#c05621' }}>
          A condition with no question chosen is dropped when this is saved.
        </small>
      )}
    </div>
  );
}

/** One widget level, with its parent so a subitem mapping can offer ancestors. */
export interface ScopeInfo {
  scope: string; label: string; parentScope: string; countKey: string;
  /** this level's conditional counts (WidgetNode.counts), mapped by key like the automatic one */
  counts?: { key: string; label: string }[];
}

// Every category in IntegrationSpec['category'] needs an entry: the fallback
// is the raw key, which reaches the picker looking like a bug.
export const CATEGORY_LABEL: Record<string, string> = {
  generic: 'General', automation: 'Automation', helpdesk: 'Help desk', crm: 'CRM', chat: 'Chat',
  project: 'Project tracking', data: 'Spreadsheets & databases', workflow: 'This form’s own workflow',
};

/**
 * The connection editors, and nothing else.
 *
 * This file used to be the Connections tab: a list with reorder arrows, an
 * Add dropdown, and an inline editor per row. The Workflow canvas replaced
 * that page entirely — removed, not hidden, because two screens writing the
 * same connections with different mental models is worse than either — and
 * what survives is the part that was never the problem: the editors
 * themselves, which the canvas now opens in the house modal.
 *
 * Nothing about them was rewritten. `ConnectionEditor` receives a connection
 * exactly as it always did; the Monday board and column pickers, the catalog
 * spec editors, the credential chooser and the template inputs with their
 * token groups are untouched.
 */

// ---- shared bits -------------------------------------------------------------

const SUBMISSION_META = [
  { key: 'submissionId', label: 'Submission ID' },
  { key: 'formName', label: 'Form name' },
  { key: 'submittedAt', label: 'Submitted at' },
  // Present only when the form required a sign-in — blank otherwise, never a
  // stand-in for an answer the respondent typed.
  { key: 'submitterEmail', label: 'Verified email (signed-in submitter)' },
  { key: 'submitterName', label: 'Verified name (signed-in submitter)' },
  { key: 'submitterProvider', label: 'Identity provider used' },
];

/** The widget levels nested BELOW `scope` — '' meaning every level on the form. */
function descendantsOf(widgetScopes: ScopeInfo[], scope: string): ScopeInfo[] {
  const out: ScopeInfo[] = [];
  const take = (parent: string) => {
    for (const w of widgetScopes) {
      if (w.parentScope !== parent) continue;
      out.push(w);
      take(w.scope);
    }
  };
  take(scope);
  return out;
}

/** The widget levels above `scope`, outermost first. */
function ancestorsOf(widgetScopes: ScopeInfo[], scope: string): ScopeInfo[] {
  const byScope = new Map(widgetScopes.map((w) => [w.scope, w]));
  const chain: ScopeInfo[] = [];
  let cur = byScope.get(scope);
  while (cur?.parentScope) {
    const parent = byScope.get(cur.parentScope);
    if (!parent) break;
    chain.unshift(parent);
    cur = parent;
  }
  return chain;
}

/**
 * Every value a template running at `scope` can draw on: that level itself,
 * each level above it, the main form, the automatic counts, and anything an
 * earlier connection hands over. Pass '' for a template that runs once per
 * submission. Shared by Monday's subitem pickers and the catalog editors so
 * both offer exactly what the server will actually fill in.
 */
export function groupsForScope(
  scope: string,
  widgetScopes: ScopeInfo[],
  fields: FieldDefinition[],
  opts: {
    extraSubmission?: { key: string; label: string }[];
    incoming?: { key: string; label: string }[];
    /**
     * The form's sections and cards. With them, a container's questions are
     * arranged the way the form is built — the Logic page's arrangement,
     * because two fields sharing a label are otherwise separable only by their
     * position in a dropdown. Without them the container stays one flat group,
     * which is all a caller that has no layout to hand can honestly offer.
     */
    sections?: SecInfo[];
    /**
     * The order this form continues — its questions, keyed `parent.<key>`,
     * as `parentTokenGroups` arranges them. Offered at every level: a subitem
     * built for a location can still name the sales rep on the order.
     */
    parent?: FieldGroup[];
  } = {}
): FieldGroup[] {
  const askable = fields.filter((f) => f.input_type !== 'file' && !asksNothing(f.input_type));
  // `withSubs` names every question by its key and marks an unpublished one,
  // so the same question reads identically in every picker in the admin, and
  // follows it with the parts of a Name or an Address — the server reads
  // `shipTo.city` here exactly as the Logic page does.
  const usable = (s: string) =>
    askable.filter((f) => f.scope === s).flatMap(withSubs).map((o) => ({ key: o.v, label: o.l }));
  /** One container, as one flat group or as the shape of the form. */
  const container = (s: string, label: string): FieldGroup[] => {
    if (!opts.sections?.length) return [{ label, options: usable(s) }];
    return groupsForContainer(s, askable, opts.sections, withSubs,
      { prefix: label, strayLabel: 'Other fields' })
      .map((g) => ({ label: g.label, options: g.opts.map((o) => ({ key: o.v, label: o.l })) }));
  };
  const label = widgetScopes.find((w) => w.scope === scope)?.label || scope;
  return [
    ...(scope ? container(scope, `This ${label}`) : []),
    ...ancestorsOf(widgetScopes, scope).flatMap((a) => container(a.scope, `${a.label} (parent level)`)),
    ...container('order', scope ? 'Form fields (main form)' : 'Form fields'),
    { label: 'Automatic counts', options: widgetScopes.map((w) => ({ key: w.countKey, label: `${w.label} count` })) },
    ...(widgetScopes.some((w) => w.counts?.length) ? [{
      label: 'Conditional counts',
      options: widgetScopes.flatMap((w) => (w.counts || []).map((c) => ({ key: c.key, label: c.label || c.key }))),
    }] : []),
    { label: 'Submission', options: [...SUBMISSION_META, ...(opts.extraSubmission || [])] },
    ...(scope ? [{ label: 'Other', options: [{ key: 'index', label: `${label} number (1, 2, 3…)` }] }] : []),
    ...(opts.incoming?.length ? [{ label: 'From other connections', options: opts.incoming }] : []),
    ...(opts.parent || []),
  ];
}

/**
 * The blocks a template can repeat, and the fields that only exist inside one.
 *
 * A per-location email still has to list that location's devices and each
 * device's lines. Those live one and two levels down, so they cannot be plain
 * tokens — they are only meaningful inside {{#each device}}. This offers the
 * loop itself, then the fields to put in it.
 */
function loopGroups(scope: string, widgetScopes: ScopeInfo[], fields: FieldDefinition[]): FieldGroup[] {
  const levels = descendantsOf(widgetScopes, scope);
  if (!levels.length) return [];
  const usable = (s: string) =>
    fields.filter((f) => f.scope === s && f.input_type !== 'file' && !asksNothing(f.input_type))
      .flatMap(withSubs).map((o) => ({ key: o.v, label: o.l }));
  return [
    {
      label: 'Repeat a block',
      options: [
        ...levels.map((l) => ({
          key: `each-${l.scope}`,
          label: `For every ${l.label}…`,
          insert: `{{#each ${l.scope}}}\n  \n{{/each}}`,
        })),
        { key: 'if-block', label: 'Only if a value was answered…', insert: '{{#if fieldKey}}\n  \n{{/if}}' },
      ],
    },
    ...levels.map((l) => ({
      label: `${l.label} — use inside {{#each ${l.scope}}}`,
      options: [
        ...usable(l.scope),
        { key: 'index', label: `${l.label} number (1, 2, 3…)` },
        { key: 'total', label: `How many ${l.label}s` },
      ],
    })),
  ];
}

/**
 * The repeating levels an address line can gather from.
 *
 * A To or a CC is one line, so the body's "Repeat a block" — an empty loop
 * with a blank middle to write in — has nowhere to go. What an address line
 * wants from a level below it is one thing: that answer from every entry. So
 * each option here is the whole loop, written out and closed, and the server's
 * address clean-up takes care of the trailing comma, the blanks and the
 * repeats. A level nested two down is wrapped in its parent's loop as well,
 * because `line` only exists inside a `device`.
 *
 * Email questions are offered; a level that asks for none falls back to its
 * plain text questions, since that is the other place an address gets typed.
 */
export function addressLoopGroups(scope: string, widgetScopes: ScopeInfo[], fields: FieldDefinition[]): FieldGroup[] {
  const below = descendantsOf(widgetScopes, scope);
  const reachable = new Set(below.map((l) => l.scope));
  return below.flatMap((l) => {
    const own = fields.filter((f) => f.scope === l.scope);
    const typed = own.filter((f) => f.input_type === 'email');
    const pool = typed.length ? typed : own.filter((f) => f.input_type === 'text');
    if (!pool.length) return [];
    const chain = [...ancestorsOf(widgetScopes, l.scope).filter((a) => reachable.has(a.scope)), l];
    const open = chain.map((c) => `{{#each ${c.scope}}}`).join('');
    const close = chain.map(() => '{{/each}}').join('');
    return [{
      label: `Every ${l.label} — one address from each`,
      options: pool.flatMap(withSubs).map((o) => ({
        key: `each-${l.scope}-${o.v}`,
        label: o.l,
        insert: `${open}{{${o.v}}}, ${close}`,
      })),
    }];
  });
}

/**
 * Catch an unbalanced {{#each}}/{{#if}} while it is being typed. The server
 * parses properly and reports the same thing on preview — this just avoids
 * making you round-trip to find out you forgot a closing tag.
 */
function templateProblem(tpl: string): string | undefined {
  const open: string[] = [];
  for (const m of String(tpl || '').matchAll(/\{\{\s*([#/])\s*(each|if|unless)\b/g)) {
    if (m[1] === '#') open.push(m[2]);
    else {
      const last = open.pop();
      if (!last) return `{{/${m[2]}}} has no matching {{#${m[2]}}}.`;
      if (last !== m[2]) return `{{/${m[2]}}} closes a {{#${last}}} — they have to match.`;
    }
  }
  return open.length ? `{{#${open[open.length - 1]}}} is never closed — add {{/${open[open.length - 1]}}}.` : undefined;
}

/** Files uploaded on the form, for the body's attachments block. */
const ATTACHMENT_TOKENS: TokenOption[] = [
  { key: 'attachmentLinks', label: 'Links to the uploaded files' },
  { key: 'attachmentNames', label: 'Uploaded file names' },
  { key: 'attachmentCount', label: 'How many files were uploaded' },
];

/** `freshdesk` + `ticketId` -> `freshdeskTicketId` — must match the server's. */
const tokenName = (type: string, key: string) =>
  type.replace(/[^a-zA-Z0-9]/g, '') + key.charAt(0).toUpperCase() + key.slice(1);

/**
 * Values another connection on this form publishes for the same repeating level
 * — the Freshdesk ticket raised for this location, ready to drop onto its
 * Monday subitem.
 */
export function incomingTokens(
  siblings: ConnectionRow[],
  catalog: CatalogEntry[],
  scope: string,
  selfId: number
): { key: string; label: string }[] {
  const byType = Object.fromEntries(catalog.map((c) => [c.type, c]));
  return siblings.flatMap((c) => {
    if (c.id === selfId || !c.enabled) return [];
    if (String((c.config as any)?.perScope || '').trim() !== scope) return [];
    return (byType[c.type]?.provides || []).map((p) => ({
      key: tokenName(c.type, p.key),
      label: `${p.label} — from "${c.name}"`,
    }));
  });
}

/**
 * Put a picked {{token}} where the caret is in a text box, and leave the caret
 * just after it — or, for a block, on its blank middle line. Attach `ref` to
 * the box; with no box to read, the token goes on the end.
 */
export function useCaretInsert(value: string, onChange: (v: string) => void) {
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const insert = (o: TokenOption) => {
    const el = ref.current;
    const text = o.insert ?? `{{${o.key}}}`;
    if (!el) { onChange(`${value}${text}`); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + text + value.slice(end));
    // For a block, land the caret on its blank middle line; otherwise just after.
    const inner = o.insert ? o.insert.indexOf('\n  \n') : -1;
    const pos = start + (inner >= 0 ? inner + 3 : text.length);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(pos, pos); });
  };
  return [ref, insert] as const;
}

/** A template box with a picker that inserts {{tokens}} at the cursor. */
export function TemplateInput({ label, hint, value, onChange, groups, multiline, rows, code, warning }: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  /** Everything the picker can insert — plain tokens and {{#each}} blocks alike. */
  groups: FieldGroup[];
  multiline?: boolean;
  rows?: number;
  /** Monospace, for hand-written HTML. */
  code?: boolean;
  warning?: string;
}) {
  const [ref, insert] = useCaretInsert(value, onChange);

  return (
    <div className="ad-field">
      <label>{label}</label>
      {multiline
        ? <textarea
            ref={ref} rows={rows || 4} value={value} onChange={(e) => onChange(e.target.value)}
            style={code ? { fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, lineHeight: 1.5 } : undefined}
          />
        : <input ref={ref} value={value} onChange={(e) => onChange(e.target.value)} />}
      {warning && <div className="ad-err" style={{ background: '#fed7d7', color: '#742a2a', marginTop: 6 }}>{warning}</div>}
      <TokenPicker groups={groups} onPick={insert} hint={hint} />
    </div>
  );
}

/**
 * "+ Insert a form value…" — the one picker every templated box carries, so
 * nobody has to remember that the sales rep's address is `{{salesRepEmail}}`.
 * A row of its own under the box, with the box's hint beside it.
 */
export function TokenPicker({ groups, onPick, hint, label }: {
  groups: FieldGroup[];
  onPick: (o: TokenOption) => void;
  hint?: string;
  /** Names the picker for a screen reader where several share one field. */
  label?: string;
}) {
  const usable = groups.filter((g) => g.options.length);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
      <select
        className="lg-add-select"
        aria-label={label}
        value=""
        onChange={(e) => {
          const [gi, oi] = e.target.value.split(':').map(Number);
          const opt = usable[gi]?.options[oi];
          if (opt) onPick(opt);
          e.currentTarget.value = '';
        }}
      >
        <option value="">+ Insert a form value…</option>
        {usable.map((g, gi) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map((o, oi) => <option key={`${o.key}-${oi}`} value={`${gi}:${oi}`}>{o.label}</option>)}
          </optgroup>
        ))}
      </select>
      {hint && <small style={{ color: '#a0aec0' }}>{hint}</small>}
    </div>
  );
}

/**
 * Splits a typed address list the way the server reads one — each entry is
 * rendered as a template of its own, then the results are split again.
 *
 * So a `{{token}}` and a whole `{{#each}}…{{/each}}` block each stay ONE
 * entry, spaces and commas inside them included: a loop cut at its own comma
 * is three fragments, none of which parses. Outside a token or a block,
 * whitespace, commas and semicolons separate addresses as they always did.
 */
export function parseAddresses(v: string): string[] {
  const out: string[] = [];
  let cur = '';
  let depth = 0;
  for (let i = 0; i < v.length;) {
    if (v.startsWith('{{', i)) {
      const close = v.indexOf('}}', i);
      const end = close < 0 ? v.length : close + 2;
      const tag = v.slice(i, end);
      if (/^\{\{\s*#/.test(tag)) depth += 1;
      else if (/^\{\{\s*\//.test(tag)) depth = Math.max(0, depth - 1);
      cur += tag;
      i = end;
    } else if (!depth && /[\s,;]/.test(v[i])) {
      if (cur) out.push(cur);
      cur = '';
      i += 1;
    } else {
      cur += v[i];
      i += 1;
    }
  }
  if (cur) out.push(cur);
  return out;
}

/**
 * A list of addresses, typed as one comma-separated box, with the form-value
 * picker under it.
 *
 * Stored as an array — the email step's `to`, `cc` and `bcc` — so the box
 * keeps its own text while it is being typed: re-deriving it from the array
 * on every keystroke would swallow the comma before the next address could
 * follow it. A pick lands at the caret as an address of its own, commas added
 * either side as needed, so it never runs into the one beside it.
 */
export function AddressInput({ id, value, onChange, groups, placeholder, pickerLabel, clear }: {
  id?: string;
  value: string[];
  onChange: (v: string[]) => void;
  groups: FieldGroup[];
  placeholder?: string;
  pickerLabel?: string;
  /** An × inside the box that clears it and puts the row away (CC, BCC). */
  clear?: { label: string; onClear: () => void };
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState(() => value.join(', '));
  // Follow a value changed from outside (a discard, a reload) — but not the
  // echo of our own typing, which parses to what is already stored.
  useEffect(() => {
    if (parseAddresses(text).join('\n') !== value.join('\n')) setText(value.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const change = (next: string) => { setText(next); onChange(parseAddresses(next)); };

  const insert = (o: TokenOption) => {
    const token = o.insert ?? `{{${o.key}}}`;
    const el = ref.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const before = text.slice(0, start);
    const after = text.slice(end);
    const lead = before.trim() && !/[\s,;]$/.test(before) ? ', ' : '';
    const tail = after.trim() && !/^[\s,;]/.test(after) ? ', ' : '';
    change(before + lead + token + tail + after);
    const pos = before.length + lead.length + token.length;
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(pos, pos); });
  };

  const box = (
    <input
      ref={ref} id={id} placeholder={placeholder}
      value={text} onChange={(e) => change(e.target.value)}
    />
  );
  return (
    <>
      {clear
        ? (
          <div className="ad-field-clearable">
            {box}
            <button type="button" className="clear" aria-label={clear.label} title={clear.label} onClick={clear.onClear}>×</button>
          </div>
        )
        : box}
      <TokenPicker groups={groups} onPick={insert} label={pickerLabel} />
    </>
  );
}

/**
 * "Use the account's credentials, or different ones for this connection?"
 *
 * The account's API keys live under Settings → Integrations and are used by
 * every form. A connection only has to ask for its own when it genuinely talks
 * to a DIFFERENT workspace — which is rare, and was previously the only option.
 *
 * Which is in use is inferred rather than stored: a connection that carries no
 * credential values of its own is using the account's. Switching back to the
 * account clears what the connection held, so nothing stale can quietly win
 * the precedence at delivery time.
 */
/**
 * Which credentials this connection delivers with.
 *
 * The two branches are "the account's" and "a different one, just for this
 * connection", and the second is the ONLY branch that renders secret fields.
 * That is what makes the Member case (Q6, canvas 7C) fall out for free: making
 * the override branch unreachable makes every secret field unreachable, so no
 * new masking is needed anywhere.
 *
 * A Member with Edit is shown the account branch selected, and the override
 * branch inert with the reason — visible rather than absent, because a missing
 * option reads as a missing feature, and "why can't I change this?" is the
 * question the sentence answers.
 */
function CredentialChoice({
  provider, title, fields, saved, cfg, setCfg, override, setOverride, canManage = true,
}: {
  provider: string;
  title: string;
  fields: CredentialField[];
  saved?: SavedCredential;
  cfg: Record<string, any>;
  setCfg: (c: Record<string, any>) => void;
  override: boolean;
  setOverride: (v: boolean) => void;
  /** false for a Member: they may pick, never read, change or add one */
  canManage?: boolean;
}) {
  const useAccount = () => {
    // Clear whatever this connection held, both the plain values and the
    // write-only ones, so the account's credentials are what resolve.
    const next = { ...cfg };
    for (const f of fields) {
      delete next[f.key];
      if (f.secret) { next[`${f.key}Cleared`] = true; next[`${f.key}__cleared`] = true; delete next[`${f.key}__set`]; }
    }
    setCfg(next);
    setOverride(false);
  };

  return (
    <div
      className="ad-field"
      style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px' }}
    >
      <label style={{ marginBottom: 6 }}>{title} account</label>
      {saved ? (
        <>
          <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontWeight: 400, marginBottom: 4 }}>
            <input type="radio" name={`cred-${provider}`} checked={!override} onChange={useAccount} />
            <span>
              Use the account's saved {title} credentials
              {saved.config?.accountSlug ? <> — <code>{String(saved.config.accountSlug)}</code></> : null}
              {saved.config?.domain ? <> — <code>{String(saved.config.domain)}</code></> : null}
              <div className="hint" style={{ margin: 0 }}>
                {canManage
                  ? <>Shared by every form. Change them in <Link to="/admin/settings/integrations">Settings → Integrations</Link>.</>
                  : 'Shared by every form. Held once for the whole account.'}
              </div>
            </span>
          </label>
          <label
            style={{
              display: 'flex', gap: 7, alignItems: 'center', fontWeight: 400,
              opacity: canManage ? 1 : 0.6,
            }}
            aria-disabled={canManage ? undefined : true}
          >
            <input
              type="radio" name={`cred-${provider}`} checked={override} disabled={!canManage}
              onChange={() => setOverride(true)}
            />
            <span>Use a different {title} account for this connection</span>
          </label>
          {!canManage && (
            <div className="hint" style={{ marginTop: 6 }}>
              ⓘ Credentials are managed by Admins. You can pick one, and you cannot see,
              change or add one.
            </div>
          )}
        </>
      ) : (
        <div className="hint" style={{ marginBottom: 6 }}>
          {canManage ? (
            <>
              Nothing saved for the account yet. Add it in{' '}
              <Link to="/admin/settings/integrations">Settings → Integrations</Link> to use it on
              every form, or enter it here for this connection only.
            </>
          ) : (
            <>Nothing saved for the account yet. Ask an Admin to add a {title} account.</>
          )}
        </div>
      )}

      {/* The only branch that renders a secret field — and unreachable for a
          Member, which is the whole of the Q6 masking story. */}
      {canManage && (override || !saved) && fields.map((f) => {
        const isSet = !!cfg[`${f.key}__set`] && !cfg[`${f.key}Cleared`] && !cfg[`${f.key}__cleared`];
        return (
          <div className="ad-field" key={f.key} style={{ maxWidth: 460, marginTop: 8, marginBottom: 0 }}>
            <label>
              {f.label}
              {f.secret && isSet && <span style={{ fontWeight: 400, color: '#718096' }}> (one is saved)</span>}
            </label>
            <input
              type={f.secret ? 'password' : 'text'}
              value={String(cfg[f.key] ?? '')}
              placeholder={f.secret && isSet ? 'Enter a new value to replace the saved one' : f.placeholder || ''}
              onChange={(e) => setCfg({
                ...cfg,
                [f.key]: e.target.value,
                ...(f.secret ? { [`${f.key}Cleared`]: false, [`${f.key}__cleared`]: false } : {}),
              })}
            />
            {f.hint && <small>{f.hint}</small>}
          </div>
        );
      })}
    </div>
  );
}

// ---- per-type config editors ----
export function ConnectionEditor({
  conn, spec, catalog, siblings, fields, widgetScopes, sections, parentGroups, parentLoops, parentFiles, otherForms, credProviders, savedCreds,
  canManageCredentials = true,
  onSaved, hideRunWhen, consumerName, bindSave, onDirtyChange,
}: {
  conn: ConnectionRow;
  spec?: CatalogEntry;
  catalog: CatalogEntry[];
  /** The form's other connections — some of them feed this one. */
  siblings: ConnectionRow[];
  fields: FieldDefinition[];
  widgetScopes: ScopeInfo[];
  /**
   * The form's sections and cards, so a value picker can be arranged the way
   * the form is built. Two questions can share a label — a "Name" in Billing
   * and a "Name" in Shipping is how forms read, not a mistake — and mapping
   * the wrong one of those to a Monday column is a silent, wrong delivery.
   */
  sections?: SecInfo[];
  /**
   * The order this form continues — its questions as `parent.<key>` tokens,
   * for a child form's step. The server already fills them (every handler
   * runs with `parent` beside the submission); this is what lets somebody
   * PICK the sales rep's email for a Monday column instead of knowing to
   * type `{{parent.salesRepEmail}}`.
   */
  parentGroups?: FieldGroup[];
  /**
   * The order's repeating levels — a list of contacts on the order — as the
   * loops that reach them: `body` for hand-written markup, `address` for a To
   * or a CC line. See `parentLoopGroups`.
   */
  parentLoops?: ParentLoops;
  /**
   * The files uploaded on the forms this one continues, as
   * `parent.attachmentLinks` and its neighbours — see `parentFileTokens`.
   */
  parentFiles?: TokenOption[];
  /** The account's other forms, for a spec field of kind 'form'. */
  otherForms: PickableForm[];
  /** What can be held per account, and what actually is. */
  credProviders: CredentialProvider[];
  savedCreds: SavedCredential[];
  /** false for a Member: the override branch, and every secret field, is inert */
  canManageCredentials?: boolean;
  onSaved: () => void;
  /**
   * Hide "Only run when…" — routing is drawn on the canvas now.
   *
   * The canvas passes this whenever the connection carries no condition. One
   * that still does keeps its editor, because the runner still honours it and
   * an admin has to be able to see and clear the thing that is quietly
   * deciding whether this step happens.
   */
  hideRunWhen?: boolean;
  /**
   * The nearest step BELOW this one that actually uses a token this step
   * publishes, when there is one — named in the "what this hands on" block.
   *
   * Passed in rather than worked out here because it is a question about the
   * drawing, and only the canvas holds the drawing. The editor's own
   * `siblings` are ancestors by design (constraint 4), so it can see what
   * feeds it and nothing at all about what it feeds.
   */
  consumerName?: string | null;
  /** Hand the save action up, so a modal footer can be the thing that calls it. */
  bindSave?: (fn: () => Promise<void>) => void;
  /**
   * Report unsaved edits up, so the modal holding this editor can ask before
   * its close button, its scrim or Escape throws them away.
   */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [name, setName] = useState(conn.name);
  const [cfg, setCfg] = useState<Record<string, any>>({ ...conn.config });
  const [runWhen, setRunWhen] = useState<ConnectionConditionGroup | null>(conn.run_when ?? null);
  // Credentials for this integration, if it has any that can live on the
  // account. "Override" is INFERRED: a connection holding its own values is
  // using them; one holding none is using the account's.
  const credFields = credProviders.find((p) => p.provider === conn.type)?.fields || [];
  const savedCred = savedCreds.find((c) => c.provider === conn.type && !c.label);
  const [credOverride, setCredOverride] = useState(() =>
    credFields.some((f) => {
      const v = (conn.config as Record<string, unknown>)[f.key];
      return (typeof v === 'string' && v.trim() !== '') || !!(conn.config as Record<string, unknown>)[`${f.key}__set`];
    }));
  /** Board and column lookups work off the account's key too — see the route. */
  const usingAccountCreds = !credOverride && !!savedCred;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ source: string; data: any } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const toast = useToast();

  const snapshot = useMemo(() => ({ name, cfg, runWhen }), [name, cfg, runWhen]);
  const { dirty, markSaved } = useDirty(snapshot);
  useEffect(() => { onDirtyChange?.(dirty); }, [onDirtyChange, dirty]);

  async function save() {
    setSaving(true); setError('');
    try {
      // A row with no question chosen is not a condition; sending it would let
      // the server drop it silently and the screen would still show it.
      const when = (runWhen?.when || []).filter((r) => r.field);
      await updateConnectionApi(conn.id, {
        name, config: cfg,
        run_when: when.length ? { match: runWhen?.match === 'any' ? 'any' : 'all', when } : null,
      });
      markSaved();
      toast(`${name} saved — in force on the live form.`);
      onSaved();
    } catch (e: any) {
      setError(e.message);
      throw e;   // so "Save and leave" stays put rather than losing the edit
    }
    finally { setSaving(false); }
  }

  // The modal's footer button is the one that saves, so it needs this
  // function; the editor still owns what saving means.
  useEffect(() => { bindSave?.(save); });

  /** Previews the config currently on screen — no need to save first. */
  async function runPreview(sample: 'latest' | 'example') {
    setPreviewing(true); setError('');
    try {
      const r = await testConnectionApi(conn.id, { config: cfg, sample });
      setPreview({ source: r.sampleSource || '', data: r.preview });
    } catch (e: any) { setError(e.message); }
    finally { setPreviewing(false); }
  }

  return (
    <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 12, paddingTop: 12 }}>
      {error && <div className="ad-err">{error}</div>}
      <div className="ad-field" style={{ maxWidth: 360 }}>
        <label>Connection name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {credFields.length > 0 && (
        <CredentialChoice
          provider={conn.type}
          title={catalog.find((c) => c.type === conn.type)?.title || conn.type}
          fields={credFields}
          saved={savedCred}
          cfg={cfg}
          setCfg={setCfg}
          override={credOverride}
          setOverride={setCredOverride}
          canManage={canManageCredentials}
        />
      )}

      {conn.type === 'webhook' && <WebhookEditor cfg={cfg} setCfg={setCfg} />}
      {conn.type === 'email' && (
        <EmailEditor
          cfg={cfg} setCfg={setCfg}
          groups={groupsForScope('', widgetScopes, fields, {
            sections, incoming: incomingTokens(siblings, catalog, '', conn.id), parent: parentGroups,
          })}
          fields={fields} widgetScopes={widgetScopes} parentLoops={parentLoops}
        />
      )}
      {conn.type === 'monday' && (
        <MondayEditor
          connId={conn.id}
          cfg={cfg} setCfg={setCfg} fields={fields} widgetScopes={widgetScopes} sections={sections}
          parentGroups={parentGroups} parentLoops={parentLoops} parentFiles={parentFiles}
          incoming={(scope) => incomingTokens(siblings, catalog, scope, conn.id)}
          usingAccountCreds={usingAccountCreds}
        />
      )}
      {!['monday', 'webhook', 'email'].includes(conn.type) && (
        <SpecEditor
          spec={spec} cfg={cfg} setCfg={setCfg} fields={fields} widgetScopes={widgetScopes}
          sections={sections} parentGroups={parentGroups} parentLoops={parentLoops} parentFiles={parentFiles}
          otherForms={otherForms}
          // Credential fields are asked for once, above — not again here.
          hideKeys={credFields.map((f) => f.key)}
        />
      )}

      {/*
        What this step hands on.

        Built from `spec.provides`, not written per step: it is the answer to
        "if this sends nothing, what does it do?" — the question the invite
        raised — and every spec-driven step can answer it the same way. Naming
        the step below that actually uses one of these turns a list of tokens
        into a sentence about this particular form.
      */}
      {!!(spec?.provides || []).length && (
        <div className="ad-provides">
          <div className="fb-overline" style={{ margin: '0 0 8px' }}>What this hands on</div>
          <div className="chips">
            {(spec!.provides || []).map((pv) => (
              <span key={pv.key} className="chip fs-mono" title={pv.label}>
                {`{{${tokenName(spec!.type, pv.key)}}}`}
              </span>
            ))}
          </div>
          <div className="note">
            — any step below this one can use them
            {consumerName ? <>. <b>{consumerName}</b> does.</> : '.'}
          </div>
        </div>
      )}

      {!hideRunWhen && <RunWhenEditor value={runWhen} fields={fields} sections={sections} onChange={setRunWhen} />}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        {!bindSave && (
          <button className="ad-btn primary" disabled={saving}
            onClick={() => { void save().catch(() => undefined); }}>{saving ? 'Saving…' : 'Save Connection'}</button>
        )}
        <span style={{ flex: 1 }} />
        <button className="ad-btn small secondary" disabled={previewing} onClick={() => runPreview('example')}>
          Preview with example data
        </button>
        <button className="ad-btn small secondary" disabled={previewing} onClick={() => runPreview('latest')}>
          Preview with latest submission
        </button>
      </div>
      {preview && <PreviewPanel source={preview.source} data={preview.data} type={conn.type} />}
    </div>
  );
}

// ---- preview rendering -------------------------------------------------------
const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  ok: { bg: '#c6f6d5', fg: '#22543d', label: 'sent' },
  empty: { bg: '#fefcbf', fg: '#744210', label: 'skipped' },
  unmapped: { bg: '#fed7d7', fg: '#742a2a', label: 'no value' },
};

function ColumnTable({ rows }: { rows: any[] }) {
  if (!rows?.length) return <div style={{ color: '#a0aec0', fontSize: 12.5 }}>No columns mapped.</div>;
  return (
    // Four columns of a dry run inside a modal: the wrapper scrolls it under
    // 900 and is INERT above, because `overflow: auto` clips `.ad-table`'s own
    // box-shadow — which is a desktop change, however small.
    <div className="ck-scroll">
    <table className="ad-table" style={{ fontSize: 12.5 }}>
      <thead><tr><th>Form value</th><th>Monday column</th><th>Sends</th><th /></tr></thead>
      <tbody>
        {rows.map((r, i) => {
          const st = STATUS_STYLE[r.status] || STATUS_STYLE.empty;
          return (
            <tr key={i}>
              <td><code>{r.fieldKey}</code></td>
              <td><code>{r.columnId}</code></td>
              <td style={{ maxWidth: 260, wordBreak: 'break-word' }}>
                {r.status === 'ok' ? <span>{typeof r.value === 'object' ? JSON.stringify(r.value) : String(r.value)}</span> : <span style={{ color: '#a0aec0' }}>—</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <span className="ad-pill" style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                {r.note && <div style={{ color: '#718096', fontSize: 11, marginTop: 3, maxWidth: 320, whiteSpace: 'normal' }}>{r.note}</div>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}

function MissingTokens({ tokens }: { tokens?: string[] }) {
  if (!tokens?.length) return null;
  return (
    <div className="ad-err" style={{ background: '#fed7d7', color: '#742a2a', marginTop: 6 }}>
      Nothing to fill {tokens.map((t) => <code key={t} style={{ marginRight: 6 }}>{`{{${t}}}`}</code>)} — check the key exists on this form (and on this level).
    </div>
  );
}

const Json = ({ value }: { value: unknown }) => (
  <pre style={{ background: '#1a202c', color: '#e2e8f0', borderRadius: 8, padding: 12, fontSize: 11.5, overflow: 'auto', maxHeight: 320 }}>
    {JSON.stringify(value, null, 2)}
  </pre>
);

/**
 * One outgoing request. When it carries an HTML body — an email — the point of
 * a preview is seeing the email, so that gets rendered and the raw JSON is
 * tucked behind a toggle. The frame is sandboxed: the body is assembled from
 * form answers, and none of it should be able to run here.
 */
function RequestPreview({ request }: { request: any }) {
  const [showJson, setShowJson] = useState(false);
  const html: string | undefined = request?.body?.description;
  const isHtml = typeof html === 'string' && /<[a-z][\s\S]*>/i.test(html);
  const to: string | undefined = request?.body?.email;
  const cc: string[] = request?.body?.cc_emails || [];

  if (!isHtml) return <Json value={request} />;
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ background: '#f7fafc', padding: '8px 10px', fontSize: 12, borderBottom: '1px solid #e2e8f0' }}>
        <div><b>Subject:</b> {request.body.subject || <span style={{ color: '#a0aec0' }}>(none)</span>}</div>
        <div><b>To:</b> {to || <span style={{ color: '#e53e3e' }}>(nobody — the token came out blank)</span>}</div>
        {cc.length > 0 && <div><b>CC:</b> {cc.join(', ')}</div>}
        <button className="ad-btn small secondary" style={{ marginTop: 6 }} onClick={() => setShowJson(!showJson)}>
          {showJson ? 'Show the email' : 'Show the raw request'}
        </button>
      </div>
      {showJson
        ? <Json value={request} />
        : (
          <iframe
            title="Email preview" sandbox="" srcDoc={html}
            className="ck-preview-frame" style={{ width: '100%', border: 0, background: '#fff' }}
          />
        )}
    </div>
  );
}

function PreviewPanel({ source, data, type }: { source: string; data: any; type: ConnectionType }) {
  if (!data) return null;
  if (type !== 'monday') {
    // A per-level connection sends one request per entry — show them separately
    // so it is obvious which location each one is for.
    const perScope = data?.perScope;
    return (
      <>
        <div className="fb-overline" style={{ margin: '14px 0 6px' }}>Preview — {source}</div>
        {perScope ? (
          <>
            <div style={{ fontSize: 12.5, color: '#718096', marginBottom: 6 }}>
              <b>{data.count}</b> would be sent — one per {perScope}
              {data.count > (data.requests?.length || 0) ? `, showing the first ${data.requests.length}` : ''}
              {data.note ? ` — ${data.note}` : ''}
            </div>
            {(data.requests || []).map((r: any, i: number) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{perScope} #{r.index}</div>
                {r.missingSettings?.length > 0 && (
                  <div className="ad-err">Not configured: {r.missingSettings.join(', ')}</div>
                )}
                {r.error ? <Json value={{ error: r.error }} /> : <RequestPreview request={r.request} />}
              </div>
            ))}
          </>
        ) : <Json value={data} />}
      </>
    );
  }
  const sub = data.subitems;
  return (
    <div style={{ marginTop: 14 }}>
      <div className="fb-overline" style={{ margin: '0 0 6px' }}>Preview — using {source}</div>
      <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, color: '#718096' }}>Item name</div>
          <div style={{ fontWeight: 700 }}>{data.itemName?.value || <span style={{ color: '#a0aec0' }}>(empty)</span>}</div>
          <div style={{ fontSize: 11.5, color: '#a0aec0' }}>template: <code>{data.itemName?.template}</code></div>
          <MissingTokens tokens={data.itemName?.missingTokens} />
        </div>

        {data.note && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: '#718096' }}>Note posted on the item</div>
            <div style={{ whiteSpace: 'pre-wrap', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 8, fontSize: 12.5 }}>
              {data.note.value || <span style={{ color: '#a0aec0' }}>(empty — nothing will be posted)</span>}
            </div>
            <MissingTokens tokens={data.note.missingTokens} />
          </div>
        )}

        <div style={{ fontSize: 12, color: '#718096', margin: '10px 0 4px' }}>Item columns</div>
        <ColumnTable rows={data.columns} />

        {sub && (
          <>
            <div style={{ fontSize: 12, color: '#718096', margin: '14px 0 4px' }}>
              Subitems — {sub.count} would be created (one per {sub.perScope})
              {sub.count > (sub.items?.length || 0) ? `, showing the first ${sub.items.length}` : ''}
            </div>
            {(sub.items || []).map((it: any, i: number) => (
              <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, marginBottom: 8, background: '#fff' }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{it.name || <span style={{ color: '#a0aec0' }}>(no name)</span>}</div>
                <MissingTokens tokens={it.missingTokens} />
                <ColumnTable rows={it.columns} />
                {it.note && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, color: '#718096' }}>Note</div>
                    <div style={{ whiteSpace: 'pre-wrap', fontSize: 12.5 }}>{it.note}</div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function WebhookEditor({ cfg, setCfg }: { cfg: Record<string, any>; setCfg: (c: Record<string, any>) => void }) {
  return (
    <>
      <div className="ad-field">
        <label>Webhook URL</label>
        <input placeholder="https://example.com/hooks/form" value={cfg.url || ''} onChange={(e) => setCfg({ ...cfg, url: e.target.value })} />
      </div>
      <div className="ad-field" style={{ maxWidth: 420 }}>
        <label>Signing secret {cfg.hasSecret && !cfg.secret ? '(currently set 🔒)' : '(optional)'}</label>
        <input
          type="password"
          placeholder={cfg.hasSecret ? 'Enter a new secret to replace it' : 'Requests are signed with X-Signature: sha256=HMAC(secret, body)'}
          value={cfg.secret || ''}
          onChange={(e) => setCfg({ ...cfg, secret: e.target.value })}
        />
        {cfg.hasSecret && (
          <label style={{ display: 'flex', gap: 5, alignItems: 'center', marginTop: 6, fontWeight: 400 }}>
            <input type="checkbox" checked={!!cfg.secretCleared} onChange={(e) => setCfg({ ...cfg, secretCleared: e.target.checked, secret: '' })} />
            Remove the secret (send unsigned)
          </label>
        )}
      </div>
    </>
  );
}

function EmailEditor({ cfg, setCfg, groups, fields, widgetScopes, parentLoops }: {
  cfg: Record<string, any>;
  setCfg: (c: Record<string, any>) => void;
  /** the form's own values, for the insert pickers */
  groups: FieldGroup[];
  fields: FieldDefinition[];
  widgetScopes: ScopeInfo[];
  /** The order's repeating levels, as loops — see ConnectionEditor. */
  parentLoops?: ParentLoops;
}) {
  // An email is sent once per submission, so every repeating level is BELOW
  // it and reachable only by looping — offered exactly as a catalog step
  // offers them: finished one-line loops for an address line, blocks to write
  // in for the message. The subject stays plain; it cannot usefully repeat.
  const addressGroups = useMemo(
    () => [...groups, ...addressLoopGroups('', widgetScopes, fields), ...(parentLoops?.address || [])],
    [groups, widgetScopes, fields, parentLoops]
  );
  const bodyGroups = useMemo(
    () => [...groups, ...loopGroups('', widgetScopes, fields), ...(parentLoops?.body || [])],
    [groups, widgetScopes, fields, parentLoops]
  );
  // For naming the document this step would attach. Every caller renders
  // inside the workspace, which is where the form lives.
  const { form } = useFormWorkspace();
  /**
   * Whether the CC and BCC rows are on screen at all.
   *
   * Seeded from what is STORED, so a connection that already copies somebody
   * opens showing it — the alternative, always starting collapsed, hides a
   * live fact about who receives this email behind a link that reads like an
   * invitation to add a new one.
   */
  const [showCc, setShowCc] = useState(!!(cfg.cc || []).length);
  const [showBcc, setShowBcc] = useState(!!(cfg.bcc || []).length);
  /** Clear the value AND put the row away — one gesture, per the × in 1c. */
  const drop = (key: 'cc' | 'bcc') => {
    const next = { ...cfg };
    delete next[key];
    setCfg(next);
    (key === 'cc' ? setShowCc : setShowBcc)(false);
  };
  return (
    <>
      <div className="ad-field">
        <label htmlFor="email-to">To</label>
        <AddressInput
          id="email-to" placeholder="{{customerEmail}}, ops@example.com" pickerLabel="Insert a form value into To"
          value={cfg.to || []} groups={addressGroups}
          onChange={(to) => setCfg({ ...cfg, to })}
        />
        {(!showCc || !showBcc) && (
          <div className="ad-addrow">
            {!showCc && <button type="button" onClick={() => setShowCc(true)}>+ Add CC</button>}
            {!showBcc && <button type="button" onClick={() => setShowBcc(true)}>+ Add BCC</button>}
          </div>
        )}
        <small>
          Comma separated. An answer can be one of them — pick it from “Insert a form value”, or
          type it: <code>{'{{customerEmail}}'}</code> writes to whoever filled the form in. One that
          comes out blank is dropped rather than failing the send.
        </small>
      </div>
      {showCc && (
        <div className="ad-field">
          <label htmlFor="email-cc">CC</label>
          <AddressInput
            id="email-cc" placeholder="orders@example.com" pickerLabel="Insert a form value into CC"
            value={cfg.cc || []} groups={addressGroups}
            onChange={(cc) => setCfg({ ...cfg, cc })}
            clear={{ label: 'Remove CC', onClear: () => drop('cc') }}
          />
          <small>Everyone on the email can see these addresses.</small>
        </div>
      )}
      {showBcc && (
        <div className="ad-field">
          <label htmlFor="email-bcc">BCC</label>
          <AddressInput
            id="email-bcc" placeholder="archive@example.com" pickerLabel="Insert a form value into BCC"
            value={cfg.bcc || []} groups={addressGroups}
            onChange={(bcc) => setCfg({ ...cfg, bcc })}
            clear={{ label: 'Remove BCC', onClear: () => drop('bcc') }}
          />
          <small>Nobody on the email sees these addresses.</small>
        </div>
      )}
      <TemplateInput
        label="Subject"
        hint="Blank uses the form name and the submission id."
        value={cfg.subjectTemplate || ''}
        onChange={(v) => setCfg({ ...cfg, subjectTemplate: v })}
        groups={groups}
      />
      <TemplateInput
        label="Message (optional)"
        hint="Blank sends the default heading. Anything here replaces it — a message to a customer should not open “New submission”."
        value={cfg.bodyTemplate || ''}
        onChange={(v) => setCfg({ ...cfg, bodyTemplate: v })}
        groups={bodyGroups}
        warning={templateProblem(cfg.bodyTemplate || '')}
        multiline
        code
      />
      <label style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={cfg.includeData !== false} onChange={(e) => setCfg({ ...cfg, includeData: e.target.checked })} />
        Include the full submission data as a table in the email
      </label>
      <label style={{ display: 'flex', gap: 5, alignItems: 'flex-start', fontSize: 13, marginTop: 8 }}>
        <input
          type="checkbox" checked={!!cfg.attachDocument} style={{ marginTop: 3 }}
          onChange={(e) => setCfg({ ...cfg, attachDocument: e.target.checked })}
        />
        <span>
          Attach the completed form as a PDF
          <small style={{ display: 'block', color: 'var(--text-muted)', marginTop: 2 }}>
            {/*
              Which PDF is the FORM's setting, not this connection's — so this
              says what will be attached rather than offering a choice that
              would let two steps send two different documents. The Document
              tab owns the answer; this owns whether it goes.
            */}
            Attaches <b>{documentSummary(workingDocumentOf(form))}</b>
            {' · '}<Link to={`/admin/forms/${form.id}/document`}>change on Document</Link>. If the PDF
            cannot be built the send fails and can be retried, rather than the email going without it.
          </small>
        </span>
      </label>
    </>
  );
}

// ---- Monday.com ----
/** One row of a mapping: a form value on the left, a Monday column on the right. */
interface MapRow { fieldKey: string; columnId: string }

/** One entry in the value picker. `insert` overrides the default `{{key}}`. */
export interface TokenOption { key: string; label: string; insert?: string }

/** Grouped options for the form-value picker. */
export interface FieldGroup { label: string; options: TokenOption[] }

/** The loops over the order's repeating levels a child form's step can insert. */
export interface ParentLoops { body: FieldGroup[]; address: FieldGroup[] }

/** Exported for its test — the mapping rows are only reachable behind a board. */
export function MappingRows({ rows, fieldGroups, columns, onChange, emptyHint }: {
  rows: MapRow[];
  fieldGroups: FieldGroup[];
  columns: MondayColumn[];
  onChange: (rows: MapRow[]) => void;
  emptyHint: string;
}) {
  const used = new Set(rows.map((r) => r.columnId).filter(Boolean));
  const allFields = fieldGroups.flatMap((g) => g.options);
  const firstUnused = () => columns.find((c) => !used.has(c.id))?.id || '';

  const set = (i: number, patch: Partial<MapRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <>
      {rows.length === 0 && <div style={{ color: '#a0aec0', fontSize: 12.5, marginBottom: 8 }}>{emptyHint}</div>}
      {rows.map((r, i) => {
        const known = allFields.some((f) => f.key === r.fieldKey);
        // One mapping is one line. The two halves are the mapping — read apart,
        // on two rows, they stop being a pair — so they shrink to fit rather
        // than wrapping, however narrow the panel holding them is.
        return (
          <div key={i} className="ck-map-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
            <select
              className="lg-ctl ck-map-half" style={{ flex: '1 1 0', minWidth: 0 }}
              value={r.fieldKey}
              onChange={(e) => set(i, { fieldKey: e.target.value })}
            >
              <option value="">— pick a form value —</option>
              {!known && r.fieldKey && <option value={r.fieldKey}>⚠ {r.fieldKey} (not on this form)</option>}
              {fieldGroups.filter((g) => g.options.length).map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </optgroup>
              ))}
            </select>
            <span className="ck-map-arrow" style={{ color: '#718096', fontSize: 13, flex: '0 0 auto' }}>→</span>
            <select
              className="lg-ctl ck-map-half" style={{ flex: '1 1 0', minWidth: 0 }}
              value={r.columnId}
              onChange={(e) => set(i, { columnId: e.target.value })}
            >
              <option value="">— pick a Monday column —</option>
              {columns.map((c) => (
                <option key={c.id} value={c.id} disabled={c.id !== r.columnId && used.has(c.id)}>
                  {c.title} ({c.type}){c.id !== r.columnId && used.has(c.id) ? ' — already mapped' : ''}
                </option>
              ))}
            </select>
            <button className="fc-x" title="Remove this mapping" onClick={() => onChange(rows.filter((_, j) => j !== i))}>×</button>
          </div>
        );
      })}
      <button
        className="fc-act"
        disabled={!columns.length}
        onClick={() => onChange([...rows, { fieldKey: '', columnId: firstUnused() }])}
      >
        + Add mapping
      </button>
    </>
  );
}

function MondayEditor({ connId, cfg, setCfg, fields, widgetScopes, sections, parentGroups, parentLoops, parentFiles, incoming, usingAccountCreds }: {
  /** the saved connection — its stored token backs lookups when none is typed */
  connId: number;
  cfg: Record<string, any>; setCfg: (c: Record<string, any>) => void;
  fields: FieldDefinition[]; widgetScopes: ScopeInfo[];
  /** The form's sections and cards, so the value pickers read like the form. */
  sections?: SecInfo[];
  /** The order this form continues, as `parent.<key>` tokens — see ConnectionEditor. */
  parentGroups?: FieldGroup[];
  /** The order's repeating levels, as loops a note can write — see ConnectionEditor. */
  parentLoops?: ParentLoops;
  /** The earlier forms' uploads, as tokens — see `parentFileTokens`. */
  parentFiles?: TokenOption[];
  /** the account's own token will answer these lookups — see mondayTokenFrom */
  usingAccountCreds: boolean;
  /** Values other connections publish for a given level (ticket ids and links). */
  incoming: (scope: string) => { key: string; label: string }[];
}) {
  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [columns, setColumns] = useState<MondayColumn[]>([]);
  const [subColumns, setSubColumns] = useState<MondayColumn[]>([]);
  const [subBoardName, setSubBoardName] = useState('');
  const [subReason, setSubReason] = useState('');
  const [loadError, setLoadError] = useState('');
  /** bumped when a freshly typed token should be tried against the API */
  const [tokenTry, setTokenTry] = useState(0);

  // The customer's own credentials, entered here when the connection is set
  // up — the server stores the token write-only and never sends it back.
  const typedToken = String(cfg.apiToken || '').trim();
  const tokenOnFile = !!cfg.apiToken__set && !cfg.apiTokenCleared;
  // The account's saved token answers board and column lookups just as well —
  // the server falls back to it — so the editor must not refuse to look.
  const hasToken = !!typedToken || tokenOnFile || usingAccountCreds;
  const authRef = () => (typedToken ? { apiToken: typedToken } : { connectionId: connId });

  useEffect(() => {
    if (!hasToken) { setBoards([]); return; }
    // A typed token waits for "Load boards" (tokenTry) so we don't fire a
    // request per keystroke; a stored one — or the account's — loads at once.
    if (typedToken && tokenTry === 0) return;
    setLoadError('');
    adminMondayBoards(authRef()).then((r) => setBoards(r.boards))
      .catch((e) => setLoadError(`Could not list boards: ${e.message}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connId, tokenOnFile, tokenTry, usingAccountCreds]);
  useEffect(() => {
    if (!cfg.boardId || !hasToken) { setColumns([]); setSubColumns([]); return; }
    adminMondayColumns(authRef(), String(cfg.boardId))
      .then((r) => setColumns(r.columns))
      .catch((e) => setLoadError(`Could not list columns: ${e.message}`));
    // Subitems live on their own Monday board — fetch ITS columns, not the parent's.
    adminMondaySubitemColumns(authRef(), String(cfg.boardId))
      .then((r) => { setSubColumns(r.columns); setSubBoardName(r.boardName); setSubReason(r.reason || ''); })
      .catch((e) => { setSubColumns([]); setSubReason(e.message); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.boardId, tokenTry]);

  const colType = (columnId: string, list: MondayColumn[]): string => {
    const t = list.find((c) => c.id === columnId)?.type || 'text';
    if (t === 'long-text' || t === 'long_text') return 'long_text';
    if (['text', 'email', 'date', 'status'].includes(t)) return t;
    if (['numbers', 'number', 'numeric', 'rating'].includes(t)) return 'number';
    return 'text';
  };

  // ---- the value pools each mapping can draw from ----
  const subScope = cfg.subitems?.widgetScope || '';
  const subScopeLabel = widgetScopes.find((w) => w.scope === subScope)?.label || subScope;

  const mainGroups: FieldGroup[] = useMemo(
    () => groupsForScope('', widgetScopes, fields, { sections, incoming: incoming(''), parent: parentGroups }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widgetScopes, fields, parentGroups]
  );
  // A subitem can be filled from its own level, any level above it, the main
  // form — which is what makes "one subitem per location, but this column comes
  // from the order header" work — and from whatever another connection raised
  // for that same location, such as its Freshdesk ticket.
  const subGroups: FieldGroup[] = useMemo(
    () => groupsForScope(subScope, widgetScopes, fields, { sections, incoming: incoming(subScope), parent: parentGroups }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subScope, widgetScopes, fields, parentGroups]
  );
  const subIncoming = incoming(subScope);
  // A note can list the uploaded files, as a ticket body can. Names and
  // columns cannot: the links are markup, and only a note renders it.
  const fileGroup: FieldGroup = useMemo(
    () => ({ label: 'Uploaded files', options: [...ATTACHMENT_TOKENS, ...(parentFiles || [])] }),
    [parentFiles]
  );
  // A note is a template like a ticket body, and the server fills a
  // {{#each}} in it the same way — so it is offered the same loops: the
  // levels below where it stands, then the order's. Reported from a
  // location-details form whose every question is asked per Location: the
  // note could list the counts and the order's answers, and not one of the
  // form's own.
  const mainNoteGroups = useMemo(
    () => [...mainGroups, fileGroup, ...loopGroups('', widgetScopes, fields), ...(parentLoops?.body || [])],
    [mainGroups, fileGroup, widgetScopes, fields, parentLoops]
  );
  const subNoteGroups = useMemo(
    () => [...subGroups, fileGroup, ...loopGroups(subScope, widgetScopes, fields), ...(parentLoops?.body || [])],
    [subGroups, fileGroup, subScope, widgetScopes, fields, parentLoops]
  );

  // A column on the item holds one value per submission. A form that asks
  // every question per Location has none to offer it — which the picker used
  // to say by offering nothing, under a heading that promised form fields.
  const asksSomething = (f: FieldDefinition) => f.input_type !== 'file' && !asksNothing(f.input_type);
  const perLevelOnly = widgetScopes.length > 0
    && fields.some(asksSomething)
    && !fields.some((f) => f.scope === 'order' && asksSomething(f));
  const levelNames = widgetScopes
    .filter((w) => fields.some((f) => f.scope === w.scope && asksSomething(f)))
    .map((w) => w.label);
  const perLevelNote = perLevelOnly && (
    <div className="hint" style={{ margin: '0 0 8px' }}>
      This form asks all its questions per {levelNames.join(', ')}, so none of them can fill one
      column on the item. Map them onto subitems below, or list them in the note with “Repeat a block”.
    </div>
  );

  // ---- config <-> rows ----
  const toRows = (map: Record<string, any> | undefined): MapRow[] =>
    Object.entries(map || {}).map(([fieldKey, v]) => ({ fieldKey, columnId: (v as any)?.columnId || '' }));

  const fromRows = (rows: MapRow[], list: MondayColumn[]) => {
    const out: Record<string, any> = {};
    for (const r of rows) {
      if (!r.fieldKey || !r.columnId) continue;
      const col = list.find((c) => c.id === r.columnId);
      out[r.fieldKey] = {
        columnId: r.columnId,
        type: colType(r.columnId, list),
        ...(col?.statusLabels && Object.keys(col.statusLabels).length ? { statusLabels: col.statusLabels } : {}),
      };
    }
    return out;
  };

  // Rows live in local state so a half-finished row (field picked, column not)
  // survives until it is complete.
  const [mainRows, setMainRows] = useState<MapRow[]>(() => toRows(cfg.columnMap));
  const [subRows, setSubRows] = useState<MapRow[]>(() => toRows(cfg.subitems?.columnMap));
  /** Columns written onto an item this connection did NOT create. */
  const [parentRows, setParentRows] = useState<MapRow[]>(() => toRows(cfg.parentColumnMap));

  const pushParent = (rows: MapRow[]) => {
    setParentRows(rows);
    setCfg({ ...cfg, parentColumnMap: fromRows(rows, columns) });
  };
  const pushMain = (rows: MapRow[]) => { setMainRows(rows); setCfg({ ...cfg, columnMap: fromRows(rows, columns) }); };
  const pushSub = (rows: MapRow[]) => {
    setSubRows(rows);
    setCfg({ ...cfg, subitems: { ...(cfg.subitems || {}), columnMap: fromRows(rows, subColumns) } });
  };

  const fileColumns = columns.filter((c) => c.type === 'file');

  return (
    <>
      {loadError && <div className="ad-err">{loadError} <span style={{ fontWeight: 400 }}>(check the API token)</span></div>}

      {/* The credentials themselves are asked for once, above, by
          CredentialChoice — from the account by default, or overridden here
          for a connection that talks to a different workspace. */}
      {typedToken && (
        <button className="fs-btn neutral" style={{ marginBottom: 14 }} type="button" onClick={() => setTokenTry((n) => n + 1)}>
          Load boards
        </button>
      )}
      {!hasToken && (
        <div className="fc-note" style={{ margin: '10px 0 14px' }}>
          No Monday.com credentials yet. Choose the account's above, or enter a token for this
          connection, and the boards on it will load.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        <div className="ad-field" style={{ minWidth: 280 }}>
          <label>Board</label>
          <select value={String(cfg.boardId || '')} onChange={(e) => setCfg({ ...cfg, boardId: e.target.value })} disabled={!hasToken}>
            <option value="">{hasToken ? 'Select a board…' : 'Add Monday.com credentials first…'}</option>
            {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        {fileColumns.length > 0 && (
          <div className="ad-field" style={{ minWidth: 220 }}>
            <label>File column (uploads land here)</label>
            <select value={cfg.fileColumnId || ''} onChange={(e) => setCfg({ ...cfg, fileColumnId: e.target.value })}>
              <option value="">— none —</option>
              {fileColumns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            {/* Only a form that continues another has earlier files to add. */}
            {cfg.fileColumnId && parentGroups && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400, marginTop: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!cfg.fileColumnParents}
                    onChange={(e) => setCfg({ ...cfg, fileColumnParents: e.target.checked || undefined })}
                  />
                  Also add the files from the forms this one continues
                </label>
                {cfg.fileColumnParents && cfg.parentMode === 'existing' && (
                  <small>
                    If the order's own Monday.com step already put its files on this item, they will be
                    there twice.
                  </small>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {!cfg.boardId ? (
        <div style={{ color: '#718096', fontSize: 13 }}>Pick a board to configure the mapping.</div>
      ) : (
        <>
          {/* Open a new item, or add to the one an earlier form already made.
              The second is how the two halves of one order stay one order. */}
          <div className="ad-field" style={{ maxWidth: 520 }}>
            <label>This connection should</label>
            <select
              value={cfg.parentMode === 'existing' ? 'existing' : 'create'}
              onChange={(e) => {
                const existing = e.target.value === 'existing';
                // What the box below SHOWS must be what gets saved: a
                // connection switched to "existing" and saved untouched used
                // to store no item id at all, and fail at delivery.
                setCfg({
                  ...cfg,
                  parentMode: existing ? 'existing' : 'create',
                  ...(existing && !cfg.parentItemIdTemplate ? { parentItemIdTemplate: '{{parent.mondayItemId}}' } : {}),
                });
              }}
            >
              <option value="create">Create a new item for every submission</option>
              <option value="existing">Add to an item another form already created</option>
            </select>
            {cfg.parentMode === 'existing' && (
              <small>
                For the second form of a two-stage order: its subitems hang under the order's own item
                instead of opening a second one.
              </small>
            )}
          </div>

          {cfg.parentMode === 'existing' ? (
            <>
              <TemplateInput
                label="Which item — its id"
                hint="Normally {{parent.mondayItemId}}: the item the order this continues created. If this comes out blank the delivery FAILS rather than making a second item."
                value={cfg.parentItemIdTemplate ?? '{{parent.mondayItemId}}'}
                onChange={(v) => setCfg({ ...cfg, parentItemIdTemplate: v })}
                groups={mainGroups}
                warning={templateProblem(cfg.parentItemIdTemplate ?? '')}
              />
              <TemplateInput
                label="Note posted on that item (optional)"
                hint="Left blank, no note is posted."
                value={cfg.updateTemplate || ''}
                onChange={(v) => setCfg({ ...cfg, updateTemplate: v })}
                groups={mainNoteGroups}
                warning={templateProblem(cfg.updateTemplate || '')}
                multiline
              />
              <div className="fb-overline" style={{ margin: '14px 0 8px' }}>
                Columns to update on that item — {columns.length} on this board
              </div>
              {perLevelNote}
              <MappingRows
                rows={parentRows}
                fieldGroups={mainGroups}
                columns={columns}
                onChange={pushParent}
                emptyHint="Nothing mapped — the item is left as it is and only the subitems are added."
              />
            </>
          ) : (
          <>
          <TemplateInput
            label="Item name"
            hint="Blank uses the submission ID."
            value={cfg.itemNameTemplate || ''}
            onChange={(v) => setCfg({ ...cfg, itemNameTemplate: v })}
            groups={mainGroups}
            warning={templateProblem(cfg.itemNameTemplate || '')}
          />
          <TemplateInput
            label="Note posted on the item (optional)"
            hint="Left blank, no note is posted."
            value={cfg.updateTemplate || ''}
            onChange={(v) => setCfg({ ...cfg, updateTemplate: v })}
            groups={mainNoteGroups}
            warning={templateProblem(cfg.updateTemplate || '')}
            multiline
          />

          <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Item columns — {columns.length} on this board</div>
          {perLevelNote}
          <MappingRows
            rows={mainRows}
            fieldGroups={mainGroups}
            columns={columns}
            onChange={pushMain}
            emptyHint="Nothing mapped yet — the item will be created with just its name."
          />
          </>
          )}

          {widgetScopes.length > 0 && (
            <>
              <div className="fb-overline" style={{ margin: '18px 0 8px' }}>Subitems (optional)</div>
              <div className="hint" style={{ margin: '0 0 8px' }}>
                Subitems are created under the item on the board above — pick the PARENT board there, not
                its “Subitems of…” board. Their columns come from that board's own subitem settings and
                are offered below.
              </div>
              <div className="ad-field" style={{ maxWidth: 340 }}>
                <label>Create one subitem per…</label>
                <select
                  value={subScope}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSubRows([]);
                    setCfg({ ...cfg, subitems: v ? { ...(cfg.subitems || {}), widgetScope: v, columnMap: {} } : undefined });
                  }}
                >
                  <option value="">— no subitems —</option>
                  {widgetScopes.map((w) => <option key={w.scope} value={w.scope}>{w.label}</option>)}
                </select>
              </div>
              {subScope && subIncoming.length > 0 && (
                <div style={{ background: '#e6fffa', border: '1px solid #b2f5ea', borderRadius: 8, padding: '8px 10px', margin: '0 0 10px', fontSize: 12.5, color: '#234e52' }}>
                  Another connection runs first for each {subScopeLabel.toLowerCase()} — its{' '}
                  {subIncoming.map((o, i) => (
                    <span key={o.key}>{i ? ', ' : ''}<code>{`{{${o.key}}}`}</code></span>
                  ))}{' '}
                  can be used in the name, the note, or mapped to a subitem column below.
                </div>
              )}
              {subScope && (
                <>
                  {/* Reported, never enforced: a rep who said 12 and a customer
                      who entered 9 have a conversation to have, and refusing
                      the submission would lose the 9 as well. */}
                  <div className="ad-field" style={{ maxWidth: 420 }}>
                    <label>Check the count against <span style={{ fontWeight: 400, color: '#a0aec0' }}>(optional)</span></label>
                    <FieldPicker
                      value={cfg.subitems?.statedCountField || ''} placeholder="— do not check —"
                      // The server reads this key off the ORDER first, by its
                      // bare key (`reconcileCounts`), so the order's questions
                      // are offered as themselves here rather than as
                      // `parent.<key>` — the one picker where that is the rule.
                      groups={[
                        ...pickerGroups(fields.filter((f) => !asksNothing(f.input_type)), sections || [], [], undefined, { repeat: false }),
                        ...(parentGroups || []).map((g) => ({
                          label: g.label,
                          opts: g.options.map((o) => ({ v: o.key.replace(/^parent\./, ''), l: o.label })),
                        })),
                      ]}
                      onChange={(v) => setCfg({ ...cfg, subitems: { ...(cfg.subitems || {}), statedCountField: v || undefined } })}
                    />
                    <small>
                      How many the ORDER said there would be. A mismatch is posted as a note on the item —
                      it never blocks the submission.
                    </small>
                  </div>
                  <TemplateInput
                    label="Subitem name"
                    value={cfg.subitems?.nameTemplate || ''}
                    onChange={(v) => setCfg({ ...cfg, subitems: { ...(cfg.subitems || {}), nameTemplate: v } })}
                    groups={subGroups}
                    warning={templateProblem(cfg.subitems?.nameTemplate || '')}
                  />
                  <TemplateInput
                    label="Note posted on each subitem (optional)"
                    value={cfg.subitems?.updateTemplate || ''}
                    onChange={(v) => setCfg({ ...cfg, subitems: { ...(cfg.subitems || {}), updateTemplate: v } })}
                    groups={subNoteGroups}
                    warning={templateProblem(cfg.subitems?.updateTemplate || '')}
                    multiline
                  />
                  {subColumns.length === 0 ? (
                    <div className="ad-err" style={{ background: '#fefcbf', color: '#744210' }}>
                      {subReason || 'No subitem columns found on this board.'}
                    </div>
                  ) : (
                    <>
                      <div style={{ color: '#718096', fontSize: 12, marginBottom: 8 }}>
                        Mapping into <b>{subBoardName}</b> — the subitem board's own {subColumns.length} columns.
                        Values can come from the {subScopeLabel.toLowerCase()} itself, any level above it, or the main form.
                      </div>
                      <MappingRows
                        rows={subRows}
                        fieldGroups={subGroups}
                        columns={subColumns}
                        onChange={pushSub}
                        emptyHint="Nothing mapped yet — subitems will be created with just their name."
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

/**
 * Settings for any integration declared in the catalog. The spec says which
 * fields exist, which are secret, and which accept {{tokens}} — so a new
 * integration needs no new UI code.
 */
function SpecEditor({ spec, cfg, setCfg, fields, widgetScopes, sections, parentGroups, parentLoops, parentFiles, otherForms, hideKeys = [] }: {
  spec?: CatalogEntry;
  cfg: Record<string, any>;
  setCfg: (c: Record<string, any>) => void;
  fields: FieldDefinition[];
  widgetScopes: ScopeInfo[];
  /** The form's sections and cards, so the value pickers read like the form. */
  sections?: SecInfo[];
  /** The order this form continues, as `parent.<key>` tokens — see ConnectionEditor. */
  parentGroups?: FieldGroup[];
  /** The order's repeating levels, as loops — see ConnectionEditor. */
  parentLoops?: ParentLoops;
  /** The earlier forms' uploads, as tokens — see `parentFileTokens`. */
  parentFiles?: TokenOption[];
  otherForms: PickableForm[];
  /** settings rendered elsewhere — the account credentials block above */
  hideKeys?: string[];
}) {
  // When the connection runs once per repeating level, its templates are filled
  // from THAT level — so the picker has to offer that level's fields, not the
  // whole form's.
  const perScope = String(cfg.perScope || '');
  const perScopeLabel = widgetScopes.find((w) => w.scope === perScope)?.label || perScope;
  const groups: FieldGroup[] = useMemo(
    () => groupsForScope(perScope, widgetScopes, fields, {
      sections,
      extraSubmission: [{ key: 'summary', label: 'Everything answered (readable list)' }],
      parent: parentGroups,
    }),
    [perScope, fields, widgetScopes, parentGroups]
  );
  // Levels BELOW this one can only be reached by looping, so they are offered
  // as insertable blocks rather than as plain tokens.
  const withLoops: FieldGroup[] = useMemo(
    () => [
      ...groups, { label: 'Uploaded files', options: [...ATTACHMENT_TOKENS, ...(parentFiles || [])] },
      ...loopGroups(perScope, widgetScopes, fields),
      ...(parentLoops?.body || []),
    ],
    [groups, perScope, widgetScopes, fields, parentLoops, parentFiles]
  );
  // An address line reaches the same levels, but as finished one-line loops.
  const withAddressLoops: FieldGroup[] = useMemo(
    () => [...groups, ...addressLoopGroups(perScope, widgetScopes, fields), ...(parentLoops?.address || [])],
    [groups, perScope, widgetScopes, fields, parentLoops]
  );

  if (!spec?.fields?.length) {
    return <div style={{ color: '#718096', fontSize: 13 }}>Loading settings…</div>;
  }

  const set = (key: string, v: unknown) => setCfg({ ...cfg, [key]: v });

  const renderField = (f: CatalogField) => {
    const stored = cfg[f.key];
    const isSet = !!cfg[`${f.key}__set`];

    // A condition group, reusing the very editor the "Only run when…" block
    // uses — one condition builder, wherever a condition is asked for.
    if (f.kind === 'condition') {
      return (
        <div className="ad-field" key={f.key}>
          <label>{f.label}</label>
          <RunWhenEditor
            value={(stored as ConnectionConditionGroup | undefined) || null}
            fields={fields}
            sections={sections}
            onChange={(v) => set(f.key, v)}
          />
          {f.hint && <small>{f.hint}</small>}
        </div>
      );
    }

    // "Invite them to…" — one of the account's other forms. Draft forms are
    // listed but refused on save at the far end, and saying so here is kinder
    // than a delivery that fails days later with the customer holding the link.
    if (f.kind === 'form') {
      const chosen = String(stored ?? '');
      const chosenForm = otherForms.find((o) => String(o.id) === chosen);
      return (
        <div className="ad-field" key={f.key} style={{ maxWidth: 460 }}>
          <label>{f.label}{f.required ? ' *' : ''}</label>
          <select value={chosen} onChange={(e) => set(f.key, e.target.value)}>
            <option value="">— choose a form —</option>
            {otherForms.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}{o.status === 'published' ? '' : ` (${o.status})`}
              </option>
            ))}
          </select>
          {f.hint && <small>{f.hint}</small>}
          {chosenForm && chosenForm.status !== 'published' && (
            <small style={{ color: '#c05621' }}>
              “{chosenForm.name}” is {chosenForm.status}. Publish it before this connection runs,
              or the invite will point at a form that opens nothing.
            </small>
          )}
          {!otherForms.length && (
            <small style={{ color: '#c05621' }}>
              This account has no other form to invite anyone to yet.
            </small>
          )}
        </div>
      );
    }

    // "One per repeating level" — the form's own levels, not a fixed list.
    if (f.kind === 'scope') {
      return (
        <div className="ad-field" key={f.key} style={{ maxWidth: 420 }}>
          <label>{f.label}</label>
          <select value={perScope} onChange={(e) => set(f.key, e.target.value)}>
            <option value="">the whole submission (one in total)</option>
            {widgetScopes.map((w) => <option key={w.scope} value={w.scope}>each {w.label}</option>)}
          </select>
          {f.hint && <small>{f.hint}</small>}
          {perScope && (
            <small style={{ color: '#2c7a7b' }}>
              The settings below are filled from each {perScopeLabel.toLowerCase()} in turn.
              {(spec.provides || []).length > 0 && (
                <> Later connections can use{' '}
                  {(spec.provides || []).map((pv, i) => (
                    <span key={pv.key}>{i ? ', ' : ''}<code>{`{{${tokenName(spec.type, pv.key)}}}`}</code></span>
                  ))} for that same {perScopeLabel.toLowerCase()}.
                </>
              )}
            </small>
          )}
        </div>
      );
    }

    if (f.kind === 'boolean') {
      return (
        <div className="ad-field" key={f.key}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 400 }}>
            <input type="checkbox" checked={stored === undefined ? f.default === true : !!stored} onChange={(e) => set(f.key, e.target.checked)} />
            {f.label}
          </label>
          {f.hint && <small>{f.hint}</small>}
        </div>
      );
    }

    if (f.kind === 'select') {
      return (
        <div className="ad-field" key={f.key} style={{ maxWidth: 320 }}>
          <label>{f.label}{f.required ? ' *' : ''}</label>
          <select value={String(stored ?? f.default ?? '')} onChange={(e) => set(f.key, e.target.value)}>
            {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
          </select>
          {f.hint && <small>{f.hint}</small>}
        </div>
      );
    }

    if (f.secret) {
      return (
        <div className="ad-field" key={f.key} style={{ maxWidth: 480 }}>
          <label>{f.label}{f.required ? ' *' : ''} {isSet ? '(stored 🔒)' : ''}</label>
          <input
            type="password"
            placeholder={isSet ? 'Enter a new value to replace it' : f.placeholder}
            value={String(stored || '')}
            onChange={(e) => set(f.key, e.target.value)}
          />
          {isSet && (
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, fontWeight: 400 }}>
              <input type="checkbox" checked={!!cfg[`${f.key}__cleared`]}
                onChange={(e) => setCfg({ ...cfg, [`${f.key}__cleared`]: e.target.checked, [f.key]: '' })} />
              Remove the stored value
            </label>
          )}
          {f.hint && <small>{f.hint}</small>}
        </div>
      );
    }

    // Hand-written markup: bigger, monospaced, and the only place loops make
    // sense — a subject line cannot usefully repeat.
    if (f.kind === 'html') {
      const v = String(stored ?? f.default ?? '');
      return (
        <TemplateInput
          key={f.key}
          label={f.label + (f.required ? ' *' : '')}
          hint={f.hint}
          value={v}
          onChange={(val) => set(f.key, val)}
          groups={withLoops}
          warning={templateProblem(v)}
          multiline
          rows={f.key === 'body' ? 14 : 5}
          code
        />
      );
    }

    if (f.kind === 'emails') {
      const v = String(stored ?? f.default ?? '');
      return (
        <TemplateInput
          key={f.key}
          label={f.label + (f.required ? ' *' : '')}
          hint={f.hint}
          value={v}
          onChange={(val) => set(f.key, val)}
          groups={withAddressLoops}
          warning={templateProblem(v)}
        />
      );
    }

    // Templated text gets the token inserter; plain text does not.
    if (f.template) {
      return (
        <TemplateInput
          key={f.key}
          label={f.label + (f.required ? ' *' : '')}
          hint={f.hint}
          value={String(stored ?? f.default ?? '')}
          onChange={(v) => set(f.key, v)}
          groups={groups}
          multiline={f.kind === 'textarea'}
        />
      );
    }

    return (
      <div className="ad-field" key={f.key} style={{ maxWidth: 480 }}>
        <label>{f.label}{f.required ? ' *' : ''}</label>
        {f.kind === 'textarea'
          ? <textarea rows={4} placeholder={f.placeholder} value={String(stored ?? f.default ?? '')} onChange={(e) => set(f.key, e.target.value)} />
          : <input placeholder={f.placeholder} value={String(stored ?? f.default ?? '')} onChange={(e) => set(f.key, e.target.value)} />}
        {f.hint && <small>{f.hint}</small>}
      </div>
    );
  };

  return (
    <>
      <div style={{ color: '#718096', fontSize: 12.5, marginBottom: 10 }}>
        {spec.blurb}
        {spec.docs && <> <a href={spec.docs} target="_blank" rel="noreferrer">API docs ↗</a></>}
      </div>
      {/* A setting that belongs to another mode is not shown. The value it
          may already hold is left alone — switching back reveals it intact. */}
      {spec.fields.filter((f) => {
        if (hideKeys.includes(f.key)) return false;
        // A setting about the earlier forms' files is shown where there are
        // earlier forms — the test Monday's own box for them uses.
        if (f.continuingOnly && !parentGroups && !parentFiles) return false;
        if (!f.showWhen) return true;
        const current = String(cfg[f.showWhen.field]
          ?? (spec.fields || []).find((x) => x.key === f.showWhen!.field)?.default
          ?? '');
        return f.showWhen.is.includes(current);
      }).map(renderField)}
    </>
  );
}
